import {
  format,
  isAfter,
  isToday,
  isYesterday,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { Fragment, useEffect } from 'react';
import { useSnapshot } from 'valtio';

import { FromStore, Job } from '@cartwhl/common/types';
import InfiniteLoadingTrigger from '@cartwhl/ui/atoms/InfiniteLoadingTrigger';
import LoadingBlock from '@cartwhl/ui/atoms/LoadingBlock';
import Text from '@cartwhl/ui/base/Text';

import {
  animationGenerationStore,
  historyStore,
  navigationStore,
} from 'stores';

import HistoryAccordion from './HistoryAccordion';

const FETCH_LIMIT = 25;

export default function GenerationHistory() {
  const { jobs, loading, done } = useSnapshot(historyStore.state);
  const { jobData, currentAnimationNumber, finishedInBackground } = useSnapshot(
    animationGenerationStore.state,
  );

  useEffect(() => {
    if (!loading && jobs === undefined) {
      historyStore.fetchHistory(FETCH_LIMIT);
    }
  }, [loading, jobs]);

  useEffect(() => {
    // This useEffect makes sure we fetch the whole history
    // this enables a client side search feature
    // Once we have serverside search we can remove this hook and infinte scroll will work as before...
    if (!loading && !done && jobs !== undefined) {
      historyStore.fetchHistory(FETCH_LIMIT);
    }
  }, [loading, done, jobs]);

  const groupedJobs = groupJobsByCreationDate(jobs);

  return (
    <>
      {jobs === undefined ? (
        <div className="grid grid-cols-1 gap-2">
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
          <LoadingBlock variant="accordion" />
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {Object.entries(groupedJobs).map(([key, jobsGroup]) => (
            <Fragment key={key}>
              <Text
                variant="studio/body/medium/sm"
                as="div"
                className="text-gray-45 flex h-[34px] items-center px-2"
              >
                {key}
              </Text>
              <HistoryAccordion
                jobs={jobsGroup}
                currentJobId={jobData?.jobID}
                currentVariationId={currentAnimationNumber}
                defaultValue={jobData?.jobID ? [jobData.jobID] : []}
                onClick={async (jobID, variationID) => {
                  navigationStore.navigate(
                    `/home?jobID=${jobID}&animationNumber=${variationID}`,
                  );
                  animationGenerationStore.fetchJob(jobID, variationID);
                }}
                finishedInBackground={finishedInBackground}
              />
            </Fragment>
          ))}
          <div className="mt-10 flex justify-center">
            <InfiniteLoadingTrigger
              loading={loading}
              done={done}
              onEnter={() => historyStore.fetchHistory(FETCH_LIMIT)}
            />
          </div>
          {done && jobs.length === 0 && (
            <Text variant="studio/body/sm" className="text-gray-60">
              Your history is empty
            </Text>
          )}
        </div>
      )}
    </>
  );
}

const DateGroups = {
  ['Today']: isToday,
  ['Yesterday']: isYesterday,
  ['Previous 7 days']: (date: string) => isAfter(date, subDays(new Date(), 7)),
  ['Previous 30 days']: (date: string) =>
    isAfter(date, subMonths(new Date(), 1)),
  ['rest']: () => true,
};

function groupJobsByCreationDate(jobs?: FromStore<Job[]>) {
  const output: Record<string, FromStore<Job>[]> = {};

  for (const job of jobs || []) {
    for (const [key, test] of Object.entries(DateGroups)) {
      if (key === 'rest') {
        if (isAfter(job.createdAt, subYears(new Date(), 1))) {
          output[format(job.createdAt, 'MMMM')] =
            output[format(job.createdAt, 'MMMM')] || [];
          output[format(job.createdAt, 'MMMM')].push(job);
        } else {
          output[format(job.createdAt, 'MMMM yyy')] =
            output[format(job.createdAt, 'MMMM yyy')] || [];
          output[format(job.createdAt, 'MMMM yyy')].push(job);
        }
      } else if (test(job.createdAt)) {
        output[key] = output[key] || [];
        output[key].push(job);
        break;
      }
    }
  }

  return output;
}
